/**
 * Meta Pixel Helper Functions for FASCINAI Landings
 * 
 * Events:
 * - PageView: Auto on load (base script)
 * - ViewContent: VSL/Sales pages
 * - Lead: Form submission (opt-in)
 * - InitiateCheckout: Checkout page (with value)
 * - Purchase: Thank you page (with value)
 */

// Page types that trigger specific events
export const LANDING_PAGE_TYPES = {
  // ViewContent events
  VIEW_CONTENT: ['vsl', 'ventas', 'sales', 'producto', 'webinar', 'sala'],
  // Lead events (trigger on form submit, not page load)
  LEAD: ['optin', 'registro', 'captacion', 'lead', 'suscripcion'],
  // InitiateCheckout events (with value)
  CHECKOUT: ['checkout', 'pago', 'compra', 'cart'],
  // Purchase events (with value)
  PURCHASE: ['gracias', 'thankyou', 'thank-you', 'confirmacion', 'success']
};

/**
 * Determines the landing page type based on slug/name
 */
export const getLandingPageType = (landingSlug) => {
  if (!landingSlug) return null;
  const slug = landingSlug.toLowerCase();
  
  for (const [type, keywords] of Object.entries(LANDING_PAGE_TYPES)) {
    if (keywords.some(keyword => slug.includes(keyword))) {
      return type;
    }
  }
  return 'VIEW_CONTENT'; // Default to ViewContent
};

/**
 * Generates the Meta Pixel base script (goes in <head>)
 */
export const generatePixelBaseScript = (pixelId) => {
  if (!pixelId) return '';
  
  return `
<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/></noscript>
<!-- End Meta Pixel Code -->
`.trim();
};

/**
 * Generates inline script for specific event tracking
 * This goes at the end of <body> or in event handlers
 */
export const generateEventScript = (eventType, params = {}) => {
  const eventMap = {
    VIEW_CONTENT: () => `fbq('track', 'ViewContent', ${JSON.stringify(params)});`,
    LEAD: () => `fbq('track', 'Lead');`,
    CHECKOUT: (p) => `fbq('track', 'InitiateCheckout', { value: ${p.value || 0}, currency: '${p.currency || 'EUR'}' });`,
    PURCHASE: (p) => `fbq('track', 'Purchase', { value: ${p.value || 0}, currency: '${p.currency || 'EUR'}' });`
  };
  
  const generator = eventMap[eventType];
  if (!generator) return '';
  
  return `<script>${generator(params)}</script>`;
};

/**
 * Generates form submission tracking script for Lead events
 * This attaches to form submit instead of page load
 */
export const generateLeadFormScript = () => {
  return `
<script>
document.addEventListener('DOMContentLoaded', function() {
  // Find all forms on the page
  var forms = document.querySelectorAll('form');
  forms.forEach(function(form) {
    form.addEventListener('submit', function(e) {
      // Fire Lead event on form submit
      if (typeof fbq === 'function') {
        fbq('track', 'Lead');
      }
    });
  });
});
</script>
`.trim();
};

/**
 * Extracts price value from string (e.g., "97€" -> 97)
 */
export const extractPriceValue = (priceString) => {
  if (!priceString) return 0;
  const match = priceString.toString().match(/[\d,.]+/);
  if (!match) return 0;
  return parseFloat(match[0].replace(',', '.'));
};

/**
 * Full injection for a landing page based on its type
 * Returns { headScript, bodyScript }
 */
export const generateFullPixelInjection = (pixelId, landingSlug, briefData = {}) => {
  if (!pixelId) return { headScript: '', bodyScript: '' };
  
  const pageType = getLandingPageType(landingSlug);
  const price = extractPriceValue(briefData?.price);
  const currency = 'EUR';
  
  // Base script always in head
  const headScript = generatePixelBaseScript(pixelId);
  
  // Body script depends on page type
  let bodyScript = '';
  
  switch (pageType) {
    case 'VIEW_CONTENT':
      bodyScript = generateEventScript('VIEW_CONTENT', {
        content_name: briefData?.product_name || '',
        content_category: briefData?.niche || ''
      });
      break;
      
    case 'LEAD':
      // Lead fires on form submit, not page load
      bodyScript = generateLeadFormScript();
      break;
      
    case 'CHECKOUT':
      bodyScript = generateEventScript('CHECKOUT', { value: price, currency });
      break;
      
    case 'PURCHASE':
      bodyScript = generateEventScript('PURCHASE', { value: price, currency });
      break;
      
    default:
      bodyScript = generateEventScript('VIEW_CONTENT');
  }
  
  return { headScript, bodyScript };
};

/**
 * Generates a complete HTML landing page with Meta Pixel injected
 * Used when exporting to GHL/WordPress
 */
export const injectPixelIntoHTML = (htmlContent, pixelId, landingSlug, briefData) => {
  if (!pixelId || !htmlContent) return htmlContent;
  
  const { headScript, bodyScript } = generateFullPixelInjection(pixelId, landingSlug, briefData);
  
  // Inject head script before </head>
  let result = htmlContent;
  if (headScript) {
    if (result.includes('</head>')) {
      result = result.replace('</head>', `${headScript}\n</head>`);
    } else {
      // No head tag, prepend to content
      result = `${headScript}\n${result}`;
    }
  }
  
  // Inject body script before </body>
  if (bodyScript) {
    if (result.includes('</body>')) {
      result = result.replace('</body>', `${bodyScript}\n</body>`);
    } else {
      // No body tag, append to content
      result = `${result}\n${bodyScript}`;
    }
  }
  
  return result;
};

export default {
  generatePixelBaseScript,
  generateEventScript,
  generateLeadFormScript,
  generateFullPixelInjection,
  injectPixelIntoHTML,
  getLandingPageType,
  extractPriceValue,
  LANDING_PAGE_TYPES
};
